import { Close } from '@mui/icons-material';
import { Box, ButtonBase, Dialog, DialogContent, IconButton, Stack, styled } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { InstagramEmbed } from 'react-social-media-embed';
import * as v from 'valibot';

import routes from '@/assets/constants/routes-no.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { useIsAfterDate } from '@/hooks/useIsAfterDate.ts';
import { useTimer } from '@/hooks/useTimer.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { CompetitionWinnerSchema, type CompetitionWinner } from '@/lib/api/requests.ts';
import { numeric } from '@/lib/schema-utils.ts';
import type { NoDate } from '@/services/new-utils.ts';
import { Button } from '@/ui/Button.tsx';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import { Snowfall } from '../../../_common/Snowfall.tsx';
import { advent2025DrawDates, getNextAdvent2025DrawDate } from '../../../config.ts';
import ForestLeft from '../_assets/forest-left.svg?react';
import ForestRight from '../_assets/forest-right.svg?react';
import House from '../_assets/house.svg?react';
import Houses from '../_assets/houses.svg?react';
import Kids from '../_assets/kids.svg?react';
import Moon from '../_assets/moon.svg?react';
import MountainsBG from '../_assets/mountains-bg.svg?react';
import RibbonBigBottom from '../_assets/ribbon-big-bottom.svg?styled';
import RibbonBigTop from '../_assets/ribbon-big-top.svg?styled';
import RibbonSmallBottom from '../_assets/ribbon-small-bottom.svg?styled';
import Snow from '../_assets/snow.svg?react';
import TileBgCanes from '../_assets/tile-bg-canes.svg?styled';
import TileBgGingerbread from '../_assets/tile-bg-gingerbread.svg?styled';
import TileBgSnow from '../_assets/tile-bg-snow.svg?styled';
import TileBgTree from '../_assets/tile-bg-tree.svg?styled';
import TreesLeft from '../_assets/trees-left.svg?react';
import TreesRight from '../_assets/trees-right.svg?react';
import { CTA } from '../_components/CTA.tsx';

const Root = styled('div')(() => ({
  display: 'grid',
  gridTemplateAreas: `
    "main"
    "below"
  `,
  '--min-height': '1000px',
  gridTemplateRows:
    'minmax(var(--min-height), min(calc(100vh - var(--top-bar-height)), 800px)) auto',
  gridTemplateColumns: '1fr auto',
  width: '100%',
  color: 'white',
  isolation: 'isolate',
}));

export type TopSectionProps = {
  winners: CompetitionWinner[];
};

export const TopSection: React.FC<TopSectionProps> = ({ winners }) => {
  return (
    <Root>
      <Illustration />
      {/* <Disclaimer /> */}
      <Box
        sx={{
          gridArea: 'below',
          backgroundColor: 'white',
          color: (theme) => theme.palette.text.default,
        }}
      />

      <div
        className="container mx-auto grid grid-cols-1 gap-12 px-6 pt-10 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
        style={{
          display: 'grid',
          // pt: { xs: 4, md: 8, lg: 10 },
          gridArea: 'main / main / below / below',
          gridTemplateRows: 'subgrid',
          // gridTemplateColumns: {
          //   xs: 'minmax(0, 1fr)',
          //   lg: 'minmax(0, 1fr) minmax(0, 1fr)',
          // },
          // gap: 6,
        }}
      >
        <Content />
        <Box
          sx={{
            gridArea: { xs: 'below', xl: 'auto' },
            // Workaround for safari
            gridColumn: { xs: '1', xl: 'auto' },
            minWidth: 0,
          }}
        >
          <Calendar winners={winners} />
        </Box>
      </div>
    </Root>
  );
};

const days = Array.from({ length: 24 }, (_, i) => i + 1);

const CalendarGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  position: 'relative',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '12px',
  maxWidth: '680px',
  width: '100%',
  marginInline: 'auto',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',

    [`& > .day-24`]: {
      gridColumn: 'span 2',
      aspectRatio: 'unset',
    },
  },
}));

const AdventWinnerSchema = v.object({
  ...CompetitionWinnerSchema.entries,
  meta: v.object({
    day: numeric(),
    instagramPostUrl: v.optional(v.string()),
    tileLine1: v.optional(v.string(), '\xa0'),
    tileLine2: v.optional(v.string(), '\xa0'),
  }),
});

type AdventWinner = v.InferOutput<typeof AdventWinnerSchema>;

const schema = v.array(AdventWinnerSchema);

type CalendarProps = {
  winners: CompetitionWinner[];
};

const Calendar: React.FC<CalendarProps> = ({ winners: winnersProp }) => {
  const winners = useMemo(() => v.parse(schema, winnersProp), [winnersProp]);
  const winnersByDay = useMemo(
    () =>
      winners.reduce(
        (acc, winner) => {
          acc[winner.meta.day] ??= [];
          acc[winner.meta.day]!.push(winner);
          return acc;
        },
        {} as Record<number, AdventWinner[] | undefined>,
      ),
    [winners],
  );
  const isAfterFirstAdventDraw = useIsAfterDate(advent2025DrawDates[0]);
  const timer = useTimer(getNextAdvent2025DrawDate());

  return (
    <CalendarGrid>
      {days.map((day) => (
        <CalendarTile key={day} day={day} winners={winnersByDay[day] || []} />
      ))}
      {!isAfterFirstAdventDraw && (
        <Box
          sx={{
            position: 'absolute',
            backgroundColor: '#EC4949',
            color: 'white',
            top: '50%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            py: 1,
          }}
        >
          <RibbonBigTop
            sx={{
              position: 'absolute',
              bottom: 'calc(100% - 1px)',
              left: '50%',
              transform: 'translateX(-50%)',
              scale: { sm: 1.5 },
              transformOrigin: '0 100%',
            }}
          />
          <Stack sx={{ textAlign: 'center', p: { xs: 1, md: 0 } }}>
            <p className="-mb-2 typography-v1-body2">Åpnes om</p>
            <p className="-mb-2 typography-v1-h1">{timer.days} dager</p>
            <p className="typography-v1-h4">
              {timer.hours} timer {timer.minutes} minutter {timer.seconds} sekunder
            </p>
          </Stack>
          <RibbonBigBottom
            sx={{
              position: 'absolute',
              top: 'calc(100% - 1px)',
              left: '50%',
              transform: 'translateX(-50%)',
              scale: { sm: 2 },
              transformOrigin: '0 0',
            }}
          />
        </Box>
      )}
    </CalendarGrid>
  );
};

type CalendarTileProps = {
  day: number;
  winners: AdventWinner[];
};

const CalendarTileRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'ended',
})<{ ended?: boolean }>(({ theme }) => ({
  backgroundColor: 'rgba(195, 208, 230, 0.30)',
  color: '#031839',
  borderRadius: '3px',
  aspectRatio: '1 / 1',
  placeContent: 'center',
  display: 'grid',
  position: 'relative',
  minWidth: 0,
  overflow: 'hidden',

  [theme.breakpoints.up('lg')]: {
    border: '2px solid #f5f9fffc',
  },

  variants: [
    {
      props: { ended: true },
      style: {
        backgroundColor: '#031839',
        color: 'white',
        cursor: 'pointer',
        [theme.breakpoints.up('lg')]: {
          borderColor: '#FDC564',
        },
      },
    },
  ],
}));

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});

// const parseAmount = (amount: string) =>
//   parseInt(amount.replace(' ', '').replace('kr', ''));

const SmallButton = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: '#FDC564',
  color: '#031839',
  borderRadius: '99999px',
  padding: '2px 8px',
  fontSize: '0.8rem',
  letterSpacing: '0.02em',
  fontWeight: 600,
  border: '1px solid #FDC564',
  transition: theme.transitions.create(['background-color']),

  '&:hover': {
    backgroundColor: 'white',
  },
}));

type CalendarTileRibbonProps = {
  date: NoDate;
};

const CalendarTileRibbon: React.FC<CalendarTileRibbonProps> = ({ date }) => {
  const { hours, minutes, seconds } = useTimer(date);

  return (
    <Stack
      sx={{
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        backgroundColor: '#E84A4A',
        color: 'white',
        py: '2px',
      }}
    >
      <p className="-mb-0.5 typography-v1-caption1">Åpnes om</p>
      <p className="typography-v1-h5">
        <span style={{ fontSize: '0.9em' }}>
          {hours}t : {minutes}m : {seconds}s
        </span>
      </p>
      <RibbonSmallBottom
        sx={{
          scale: 2,
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          transformOrigin: '0 0',
        }}
      />
    </Stack>
  );
};

const CalendarTile: React.FC<CalendarTileProps> = ({ day, winners }) => {
  const hasWinners = winners.length > 0;
  // const amount = winners.reduce(
  //   (acc, winner) => acc + parseAmount(winner.amount),
  //   0,
  // );
  const date = advent2025DrawDates[day - 1];
  const nextDrawDate = getNextAdvent2025DrawDate();
  const isAfterFirstDraw = useIsAfterDate(advent2025DrawDates[0]);
  const [open, setOpen] = useState(false);
  const ended = useIsAfterDate(date);

  if (hasWinners) {
    return (
      <>
        <CalendarTileRoot
          className={`day-${day}`}
          ended
          onClick={() => setOpen(true)}
          sx={{ textAlign: 'center', py: 0.1 }}
        >
          <Stack>
            <p className="typography-v1-caption1">
              <b>{dateFormatter.format(new Date(2025, 11, day))}</b>
            </p>
            <p className="typography-v1-caption1">
              <b>{winners[0].meta.tileLine1}</b>
            </p>
            <p className="mb-2 typography-v1-caption1">{winners[0].meta.tileLine2}</p>
            <SmallButton onClick={() => setOpen(true)}>Se vinner</SmallButton>
          </Stack>
        </CalendarTileRoot>

        <WinnerDialog day={day} winners={winners} open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  if (ended) {
    return (
      <>
        <CalendarTileRoot className={`day-${day}`} ended onClick={() => setOpen(true)}>
          <Stack>
            <p className="mb-3 typography-v1-caption1">
              {dateFormatter.format(new Date(2025, 11, day))}
            </p>
            <p className="mb-4 typography-v1-caption1">
              <b style={{ fontSize: '1.1em' }}>Trukket</b>
            </p>
            <SmallButton onClick={() => setOpen(true)}>Se vinner</SmallButton>
          </Stack>
        </CalendarTileRoot>

        <NoWinnerYetDialog day={day} open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  const Background = {
    6: TileBgCanes,
    8: TileBgSnow,
    13: TileBgGingerbread,
    24: TileBgTree,
  }[day];

  return (
    <CalendarTileRoot className={`day-${day}`}>
      <p className="typography-v1-h1">{day}</p>
      {Background && (
        <Background
          sx={{
            position: 'absolute',
            width: 'auto',
            height: '100%',
            aspectRatio: '1 / 1',
            display: { xs: 'block', lg: 'none' },
          }}
        />
      )}
      {date === nextDrawDate && isAfterFirstDraw && <CalendarTileRibbon date={date} />}
    </CalendarTileRoot>
  );
};

type WinnerDialogProps = {
  day: number;
  winners: AdventWinner[];
  open: boolean;
  onClose: () => void;
};

const WinnerDialog: React.FC<WinnerDialogProps> = ({ day, winners, open, onClose }) => {
  const winner = winners[0];

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#031839',
          color: 'white',
          colorScheme: 'dark',
          p: 2,
          borderRadius: '16px',
          mx: 0,
          width: 'calc(100% - 24px)',
        },
      }}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <IconButton sx={{ ml: 'auto' }} size="small" onClick={onClose}>
        <Close fontSize="small" />
      </IconButton>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        <Stack>
          <p className="typography-v1-body4">Dagens vinner</p>
          <p className="mb-6 typography-v1-h2">{day}. desember</p>

          {winner.meta.instagramPostUrl ? (
            <Box sx={{ mb: 2, minHeight: { xs: '544px', md: '626px' } }}>
              <InstagramEmbed url={winner.meta.instagramPostUrl} width={326} />
            </Box>
          ) : (
            <p className="mb-8 text-center typography-v1-body4">
              {winner.name}
              <br />
              {winner.age}, {winner.city}
              <br />
              Gratulerer!
            </p>
          )}

          <Button color="yellow" onClick={onClose} sx={{ width: 'auto', minWidth: '200px' }}>
            OK
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

type NoWinnerYetDialogProps = {
  day: number;
  open: boolean;
  onClose: () => void;
};

const NoWinnerYetDialog: React.FC<NoWinnerYetDialogProps> = ({ day, open, onClose }) => {
  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#031839',
          color: 'white',
          colorScheme: 'dark',
          p: 2,
          borderRadius: '16px',
          mx: 0,
          width: 'calc(100% - 24px)',
        },
      }}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <IconButton sx={{ ml: 'auto' }} size="small" onClick={onClose}>
        <Close fontSize="small" />
      </IconButton>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        <Stack>
          <p className="typography-v1-body4">Dagens vinner</p>
          <p className="mb-6 typography-v1-h2">{day}. desember</p>

          <p className="mb-6 text-center typography-v1-body4">
            Luken er åpnet.
            <br />
            Vinneren publiseres så snart vi har
            <br /> oppnådd kontakt.
          </p>

          <Button color="yellow" onClick={onClose} sx={{ width: 'auto', minWidth: '200px' }}>
            OK
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const Gold = styled('span')({ color: '#FDC564', fontWeight: 700 });

const Content = () => {
  return (
    <Box>
      <Box
        component="h1"
        sx={{
          mb: 3,
          fontSize: { xs: '2.25rem', sm: '3rem', xl: '3.5rem' },
          lineHeight: '1.2',
          // textWrap: 'balance',
          fontWeight: 700,
        }}
      >
        Sjekk om du kan få lavere rente og delta i Scooprs julekalender
      </Box>

      {/* <p className="typography-v1-body3 mb-8 font-extrabold">
        Denne trekningen er avsluttet.
      </p> */}
      <p className="mb-3 typography-v1-body3 font-semibold">
        Fra <b>1. - 23. desember</b> trekkes en vinner <b>hver dag</b> som får inntil{' '}
        <Gold>100&nbsp;000&nbsp;kr</Gold> nedbetalt på sin kredittkortgjeld.
      </p>

      <p className="mb-6 typography-v1-body3 font-semibold">
        <b>24. desember</b> trekkes <b>én vinner</b> som får nedbetalt inntil{' '}
        <Gold>500&nbsp;000&nbsp;kr</Gold> på forbruks- og kredittkortgjeld.
      </p>

      <NumberedList
        sx={{ mb: 3 }}
        renderNumber={(props) => (
          <NumberCircle
            sx={{
              fontSize: 14,
              height: 24,
              backgroundColor: '#FBFDFF',
              color: '#354661',
              fontWeight: 700,
            }}
            {...props}
          />
        )}
        renderLabel={(props) => <div {...props} className="typography-v1-body4" />}
      >
        <NumberedListItem>Logg inn med BankID</NumberedListItem>
        <NumberedListItem>Søk om lavere rente</NumberedListItem>
        <NumberedListItem>Delta i julekalenderen</NumberedListItem>
      </NumberedList>

      <CTA
        sx={{ width: 'auto', mb: 3, display: 'block', mr: 'auto', ml: 0 }}
        onClick={() => {
          sendUserPortalEvent('adv25_competition_cta_top_clicked');
        }}
      />

      <Link
        to={routes.ADVENT_2025_COMPETITION_TERMS}
        className="mb-6 block typography-v1-body4 text-[#D1ECFE] underline"
      >
        Se vilkår og betingelser for deltagelse
      </Link>

      <div className="typography-v1-body4">
        <LoanExampleText />
      </div>
    </Box>
  );
};

const Illustration = () => {
  return (
    <div
      className="relative isolate -z-10 overflow-hidden"
      style={{
        gridArea: 'main',
        background: 'linear-gradient(0deg, #608FDA 0%, #072453 50%)',
      }}
    >
      <MountainsBG
        className="absolute bottom-0 left-[-120%] w-[300%] sm:right-0 sm:-left-1/2 sm:w-[200%] md:left-0 md:w-full"
        preserveAspectRatio="none"
      />
      <Moon className="absolute right-1/2 -bottom-4 scale-75 max-lg:translate-x-1/2 md:scale-100 lg:right-[45%] lg:bottom-0 [&_.santa]:origin-center [&_.santa]:scale-75 md:[&_.santa]:scale-100" />
      <ForestRight className="absolute bottom-16 left-7/12 2xl:right-0 2xl:left-auto" />
      <ForestLeft className="absolute right-8/12 bottom-16" />
      <Snow className="absolute bottom-0 left-0 w-[120%] sm:w-full" preserveAspectRatio="none" />

      <Houses className="absolute right-5/12 bottom-18 origin-bottom-right scale-50 sm:scale-75 xl:right-[45%] xl:scale-100" />

      <Houses className="absolute right-4 bottom-24 origin-bottom-right scale-50 sm:scale-75 md:right-8 md:bottom-36 xl:scale-125" />

      <House className="absolute bottom-0 left-8 origin-bottom-left scale-50 sm:left-16 sm:scale-75 xl:left-20 xl:scale-100" />
      <Kids className="absolute bottom-0 left-4/12 origin-bottom-right scale-50 sm:scale-75 xl:scale-100" />
      <TreesLeft className="absolute bottom-0 left-0 origin-bottom-left scale-50 sm:scale-75 xl:scale-100" />
      <TreesRight className="absolute right-0 bottom-0 origin-bottom-right scale-50 sm:scale-75 xl:scale-100" />

      <Snowfall sx={{ position: 'absolute', inset: 0 }} />
    </div>
  );
};
